import { useEffect, useRef, useState } from 'react';
import { useCheckSession } from '@serpro/login-cidadao-volto/hooks/useCheckSession';
import TagManager from '@sooro-io/react-gtm-module';
import {
  createClientLogger,
  getClientEnvValue,
} from 'govbr.frontend.policy/helpers';
import { withLgpdCookie } from 'govbr.lgpd.plugin/components';
import { useSelector } from 'react-redux';

const GTM_CONFIG = {
  debugEnvKey: 'RAZZLE_GTM_DEBUG_MODE',
  gtmCodeEnvKey: 'RAZZLE_GTM_CODE',
  productionHostname: 'www.gov.br',
  productionFallbackGtmId: 'GTM-KP4X8DHR',
  consentGranted: 'granted',
  consentDenied: 'denied',
  consentWaitForUpdateMs: 500,
  consentGrantedEvent: {
    event: 'cookie_consent_granted',
    usage_stats: 'true',
  },
};

const debugLog = createClientLogger({
  prefix: '[GTM]',
  envKey: GTM_CONFIG.debugEnvKey,
});

const pushConsentUpdate = (performanceCookies) => {
  window.dataLayer = window.dataLayer || [];
  const consentState = performanceCookies
    ? GTM_CONFIG.consentGranted
    : GTM_CONFIG.consentDenied;

  window.dataLayer.push([
    'consent',
    'update',
    {
      analytics_storage: consentState,
      ad_storage: consentState,
      ad_user_data: consentState,
      ad_personalization: consentState,
    },
  ]);
};

const GTM = ({ performanceCookies }) => {
  const remove_analytics = useSelector(
    (state) => state.site_properties?.data?.remove_analytics,
  );

  const { fetchData, data, loaded, loading, error } = useCheckSession();
  const gtmInitializedRef = useRef(false);
  const lastConsentRef = useRef(null);
  const lastIdentifiedUserRef = useRef(null);
  const sessionFetchedRef = useRef(false);
  const [gtmReady, setGtmReady] = useState(false);

  // Ver a nota equivalente em BarraGovbr: sem este guard, o `fetchData` do
  // useCheckSession muda de identidade a cada dispatch e este effect redispara
  // indefinidamente quando o backend responde 200 sem `headerBarToken`.
  useEffect(() => {
    if (sessionFetchedRef.current) {
      return;
    }
    sessionFetchedRef.current = true;
    debugLog('Fetching session data...');
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    debugLog('Session state changed', {
      loaded,
      loading,
      error,
      analyticsId: data?.analyticsId ?? null,
      hasUserSession: !!data,
    });
  }, [loaded, loading, error, data]);

  useEffect(() => {
    if (!__CLIENT__) return;
    if (typeof remove_analytics === 'undefined') {
      debugLog('Waiting site_properties.remove_analytics to be defined');
      return;
    }
    if (remove_analytics) {
      debugLog('GTM blocked by remove_analytics=true');
      return;
    }
    if (gtmInitializedRef.current) {
      debugLog('GTM already initialized. Skipping rerun.');
      return;
    }
    if (!loaded) {
      debugLog('Waiting for GovBR session check to finish before GTM init');
      return;
    }

    const configuredGtmId = getClientEnvValue(GTM_CONFIG.gtmCodeEnvKey);
    const gtmId =
      configuredGtmId ||
      (window.location.hostname === GTM_CONFIG.productionHostname
        ? GTM_CONFIG.productionFallbackGtmId
        : '');

    if (!gtmId) {
      debugLog('GTM not initialized because gtmId is empty', {
        hostname: window.location.hostname,
        envValue: configuredGtmId,
      });
      return;
    }

    window.dataLayer = window.dataLayer || [];
    debugLog('Initializing GTM', { gtmId });
    window.dataLayer.push([
      'consent',
      'default',
      {
        ad_storage: GTM_CONFIG.consentDenied,
        analytics_storage: GTM_CONFIG.consentDenied,
        ad_user_data: GTM_CONFIG.consentDenied,
        ad_personalization: GTM_CONFIG.consentDenied,
        wait_for_update: GTM_CONFIG.consentWaitForUpdateMs,
      },
    ]);

    TagManager.initialize({ gtmId });
    gtmInitializedRef.current = true;
    setGtmReady(true);
  }, [remove_analytics, loaded]);

  useEffect(() => {
    if (!__CLIENT__) return;
    if (!gtmReady) return;

    const previousConsent = lastConsentRef.current;
    if (previousConsent === performanceCookies) return;

    pushConsentUpdate(performanceCookies);
    debugLog('Consent updated', {
      consentState: performanceCookies
        ? GTM_CONFIG.consentGranted
        : GTM_CONFIG.consentDenied,
    });

    if (performanceCookies && previousConsent !== true) {
      window.dataLayer.push(GTM_CONFIG.consentGrantedEvent);
      debugLog('Consent granted event pushed to dataLayer');
    }

    lastConsentRef.current = performanceCookies;
  }, [performanceCookies, gtmReady]);

  useEffect(() => {
    if (!__CLIENT__) return;
    if (!gtmReady) return;

    window.gov_data = window.gov_data || {};

    const analyticsId = data?.analyticsId || null;

    if (!analyticsId) {
      if (window.gov_data.analyticsId) {
        delete window.gov_data.analyticsId;
        debugLog('analyticsId removed from window.gov_data');
      }
      lastIdentifiedUserRef.current = null;
      return;
    }

    window.gov_data.analyticsId = analyticsId;

    if (!performanceCookies) return;
    if (lastIdentifiedUserRef.current === analyticsId) return;

    window.dataLayer.push({
      event: 'user_identified',
      user_id: analyticsId,
    });
    debugLog('user_identified pushed', { analyticsId });

    lastIdentifiedUserRef.current = analyticsId;
  }, [data, performanceCookies, gtmReady]);

  return null;
};

export default withLgpdCookie(GTM);
